/* eslint-disable react/prop-types */
import { useState, useRef, useEffect } from "react"
import styled from "styled-components"
import { motion } from "framer-motion"
import CloseIcon from '@mui/icons-material/Close';

const Container = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  cursor: none;

  @media (max-width: 768px) {
    cursor: default;
  }
`

const Image = styled.img`
  display: block;
  object-fit: contain;
  width: 700px;
  height: 500px;
  @media screen and (max-width: 1000px) {
    width: 100%;
  }
  @media screen and (max-width: 768px) {
    margin-top: 60px;
    height: 300px;
    width: 100%;
  }
  @media screen and (max-width: 408px) {
    margin-top: 60px;
    height: 200px;
    object-fit: inherit;
  }

`

const MagnifierGlass = styled(motion.div)`
  position: absolute;
  border: 3px solid #fff;
  border-radius: 50%;
  pointer-events: none;
  background-repeat: no-repeat;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.4);
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileZoomView = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 769px) {
    display: none;
  }
`

const MobileZoomImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  color: #000;
  border: none;
  border-radius: 99999999px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  z-index: 1001;
`

// Indicador visual para mostrar el nivel de zoom actual
const ZoomIndicator = styled(motion.div)`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  opacity: 0.8;
`

export default function SuperZoomMagnifier({
  src,
  alt = "Image",
  width = "100%",
  height = "auto",
  magnifierSize = 180,
  zoomLevel = 15,
}) {
  const [showMagnifier, setShowMagnifier] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [backgroundPosition, setBackgroundPosition] = useState("0% 0%")
  const [showMobileZoom, setShowMobileZoom] = useState(false)
  const containerRef = useRef(null)
  const imageRef = useRef(null)
  const [isMobile, setIsMobile] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [showZoomIndicator, setShowZoomIndicator] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => {
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  const handleMouseEnter = () => {
    if (!isMobile && imageLoaded) {
      setShowMagnifier(true)
      setShowZoomIndicator(true)

      // Ocultar el indicador de zoom después de 2 segundos
      setTimeout(() => {
        setShowZoomIndicator(false)
      }, 2000)
    }
  }

  const handleMouseLeave = () => {
    setShowMagnifier(false)
    setShowZoomIndicator(false)
  }

  const handleMouseMove = (e) => {
    if (!containerRef.current || !imageRef.current || !imageLoaded) return

    const { left, top, width, height } = containerRef.current.getBoundingClientRect()

    // Calculate cursor position relative to the container
    const x = e.clientX - left
    const y = e.clientY - top

    // Calculate position as percentage for background position
    // This is crucial for making the zoom work correctly
    const xPercent = (x / width) * 100
    const yPercent = (y / height) * 100

    // Update magnifier position (centered on cursor)
    setPosition({ x, y })

    // Update background position for the zoomed view
    setBackgroundPosition(`${xPercent}% ${yPercent}%`)
  }

  const handleImageClick = () => {
    if (isMobile) {
      setShowMobileZoom(true)
    }
  }

  const closeMobileZoom = () => {
    setShowMobileZoom(false)
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  return (
    <>
      <Container
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onClick={handleImageClick}
        style={{ width, height }}
      >
        <Image ref={imageRef} src={src || "/placeholder.svg"} className="main-image" alt={alt} onLoad={handleImageLoad} />

        {showMagnifier && (
          <MagnifierGlass
            size={magnifierSize}
            style={{
              left: position.x - magnifierSize / 2,
              top: position.y - magnifierSize / 2,
              backgroundImage: `url(${src})`,
              backgroundPosition,
              // Super high zoom level
              backgroundSize: `${zoomLevel * 100}%`,
            }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          />
        )}

        {showZoomIndicator && (
          <ZoomIndicator
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 0.8, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {zoomLevel}x Zoom
          </ZoomIndicator>
        )}
      </Container>

      {showMobileZoom && (
        <MobileZoomView
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CloseButton onClick={closeMobileZoom}>

          <CloseIcon>×</CloseIcon>

          </CloseButton>
          <MobileZoomImage src={src} alt={alt} />
        </MobileZoomView>
      )}
    </>
  )
}

